import { MetricCard, Skeleton } from "@/components/reusable";
import type { AnalyticsSummary } from "@/types/analytics";

interface MetricsStripProps {
  summary: AnalyticsSummary | null;
  loading?: boolean;
}

function fmtUsdCompact(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtCount(n: number): string {
  return Math.round(n).toLocaleString();
}

function fmtPercent(n: number): string {
  return `${n.toFixed(1)}%`;
}

export function MetricsStrip({ summary, loading = false }: MetricsStripProps) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="bg-white border border-[var(--color-border-light)] rounded-[10px] px-4 py-3.5 flex flex-col gap-2"
          >
            <Skeleton width="w-20" height="h-2.5" />
            <Skeleton width="w-24" height="h-5" />
            <Skeleton width="w-16" height="h-2" />
          </div>
        ))}
      </div>
    );
  }

  const passRatePct = summary.dqPassRate * 100;
  const needsReview = Math.max(0, Math.round(summary.totalVessels * (1 - summary.dqPassRate)));
  const perVessel = summary.totalVessels > 0 ? summary.totalPremiumUsd / summary.totalVessels : 0;
  const rol =
    summary.totalSumsInsuredUsd > 0
      ? (summary.totalPremiumUsd / summary.totalSumsInsuredUsd) * 100
      : 0;
  const agedPct = summary.agedVesselPercent * 100;

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
      <MetricCard
        label="Total vessels"
        numericValue={summary.totalVessels}
        formatValue={fmtCount}
        sub={
          summary.newSinceLastImport > 0
            ? `+${summary.newSinceLastImport} since last import`
            : undefined
        }
        subTone="up"
      />
      <MetricCard
        label="Total sums insured"
        numericValue={summary.totalSumsInsuredUsd}
        formatValue={fmtUsdCompact}
        sub="+$84M on renewal"
        subTone="up"
      />
      <MetricCard
        label="DQ pass rate"
        numericValue={passRatePct}
        formatValue={fmtPercent}
        sub={`${needsReview} vessel${needsReview === 1 ? "" : "s"} need review`}
        subTone={summary.dqPassRate >= 0.95 ? "up" : summary.dqPassRate >= 0.8 ? "warn" : "danger"}
      />
      <MetricCard
        label="Total premium"
        numericValue={summary.totalPremiumUsd}
        formatValue={fmtUsdCompact}
        sub={`$${Math.round(perVessel).toLocaleString()} per vessel`}
        subTone="neutral"
        detail={`Rate on line: ${rol.toFixed(2)}% of SI`}
      />
      <MetricCard
        label="Fleet age risk"
        numericValue={summary.agedVesselCount}
        formatValue={fmtCount}
        sub="vessels over 20 years old"
        subTone={summary.agedVesselPercent > 0.15 ? "warn" : "neutral"}
        detail={`${agedPct.toFixed(1)}% of fleet`}
      />
    </div>
  );
}
